import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Circle, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const INK = '#0F1729';
const BRAND = '#1F4FE5';
const SUN = '#E69400';
const MUTED = '#8A93A7';
const PAPER = '#FBF7F0';

// Helper component to imperatively fit bounds / fly to from outside MapContainer
function MapController({ stops, routes, selectedHotZoneIds, hotZones, recenterSignal }) {
  const map = useMap();
  const didFitOnceRef = useRef(false);

  // Fit to all data on first load
  useEffect(() => {
    if (didFitOnceRef.current) return;
    const pts = [];
    for (const s of stops) {
      if (Number(s.lat) && Number(s.lng)) pts.push([s.lat, s.lng]);
    }
    for (const r of routes) {
      for (const c of r.coordinates || []) {
        if (c[0] && c[1]) pts.push([c[1], c[0]]);
      }
    }
    if (pts.length === 0) return;
    const bounds = L.latLngBounds(pts);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    didFitOnceRef.current = true;
  }, [map, stops, routes]);

  // Fly to selected hot zones
  useEffect(() => {
    if (selectedHotZoneIds.length === 0) return;
    const selected = hotZones.filter(z => selectedHotZoneIds.includes(z.id));
    if (selected.length === 0) return;
    const pts = selected.map(z => [z.lat, z.lng]);
    if (pts.length === 0) return;
    const bounds = L.latLngBounds(pts).pad(0.2);
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14, animate: true, duration: 0.8 });
  }, [map, selectedHotZoneIds, hotZones]);

  // Recenter when signal changes
  useEffect(() => {
    if (recenterSignal === 0) return;
    const pts = [];
    for (const s of stops) {
      if (Number(s.lat) && Number(s.lng)) pts.push([s.lat, s.lng]);
    }
    for (const r of routes) {
      for (const c of r.coordinates || []) {
        if (c[0] && c[1]) pts.push([c[1], c[0]]);
      }
    }
    if (pts.length === 0) return;
    const bounds = L.latLngBounds(pts);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13, animate: true });
  }, [map, recenterSignal, stops, routes]);

  return null;
}

export default function McpMapLibre({
  mode = 'stop',
  radiusMi = 1,
  stops = [],
  routes = [],
  trucks = [],
  selectedIds = [],
  onToggleSelection,
  heatmapMode = 'off',
  hotZones = [],
  selectedHotZoneIds = [],
  onToggleHotZone,
  onHotZoneStopsSelected,
  className = '',
  style = {},
}) {
  const [mapCenter, setMapCenter] = useState(null);
  const [recenterSignal, setRecenterSignal] = useState(0);

  // Default center (US center)
  const defaultZoom = 4;

  // Compute a center from data for initial display
  const initialCenter = useMemo(() => {
    const defaultCenter = [38.5, -96.5];
    const pts = [];
    for (const s of stops) {
      if (Number(s.lat) && Number(s.lng)) pts.push([s.lat, s.lng]);
    }
    for (const r of routes) {
      for (const c of r.coordinates || []) {
        if (c[0] && c[1]) pts.push([c[1], c[0]]);
      }
    }
    if (pts.length === 0) return defaultCenter;
    const lat = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const lng = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    return [lat, lng];
  }, [stops, routes]);

  const radiusMeters = radiusMi * 1609.34;

  // Truck color lookup
  const truckColor = useMemo(() => {
    const map = {};
    for (const t of trucks) map[t.id] = t.color || BRAND;
    return map;
  }, [trucks]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const hotZoneStopIds = useMemo(() => {
    const set = new Set();
    for (const z of hotZones) {
      if (selectedHotZoneIds.length === 0 || selectedHotZoneIds.includes(z.id)) {
        for (const id of z.stopIds || []) set.add(id);
      }
    }
    return set;
  }, [hotZones, selectedHotZoneIds]);

  // Update map center label when map moves
  function handleMoveEnd(e) {
    const c = e.target.getCenter();
    setMapCenter({ lat: c.lat, lng: c.lng });
  }

  return (
    <div
      className={`relative w-full overflow-hidden rounded-3xl border-2 border-paper-300 bg-paper-50 shadow-soft ${className}`}
      style={{ minHeight: 500, ...style }}
    >
      <MapContainer
        center={initialCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%', background: PAPER }}
        scrollWheelZoom={true}
        whenReady={(e) => {
          const map = e.target;
          const c = map.getCenter();
          setMapCenter({ lat: c.lat, lng: c.lng });
          map.on('moveend', handleMoveEnd);
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapController
          stops={stops}
          routes={routes}
          selectedHotZoneIds={selectedHotZoneIds}
          hotZones={hotZones}
          recenterSignal={recenterSignal}
        />

        {/* Hot zone circles (stop mode only) */}
        {mode === 'stop' && hotZones.map((z) => {
          if (!Number(z.lat) || !Number(z.lng)) return null;
          const selected = selectedHotZoneIds.includes(z.id);
          return (
            <CircleMarker
              key={z.id}
              center={[z.lat, z.lng]}
              radius={selected ? 14 : 9}
              pathOptions={{
                color: selected ? SUN : MUTED,
                fillColor: selected ? SUN : MUTED,
                fillOpacity: selected ? 0.25 : 0.1,
                weight: selected ? 2.5 : 1.5,
              }}
              eventHandlers={{
                click: () => {
                  onToggleHotZone?.(z.id);
                  onHotZoneStopsSelected?.(z.id);
                },
              }}
            >
              <Tooltip direction="top">
                <div style={{ minWidth: 140 }}>
                  <strong>{z.stopIds?.length || 0} stops</strong>
                </div>
              </Tooltip>
            </CircleMarker>
          );
        })}

        {/* Routes (route mode) */}
        {mode === 'route' && routes.map((r) => {
          if (!r.coordinates || r.coordinates.length < 2) return null;
          const positions = r.coordinates.map(c => [c[1], c[0]]);
          const selected = selectedSet.has(r.id) || selectedSet.has(r.truckId);
          const color = truckColor[r.truckId] || BRAND;
          return (
            <React.Fragment key={r.id}>
              <Polyline
                positions={positions}
                color={color}
                weight={selected ? 6 : 3}
                opacity={selected ? 0.95 : 0.5}
                eventHandlers={{ click: () => onToggleSelection?.(r.id) }}
              />
              {/* Start marker */}
              <CircleMarker
                center={positions[0]}
                radius={selected ? 10 : 7}
                pathOptions={{
                  color: '#ffffff',
                  fillColor: color,
                  fillOpacity: 1,
                  weight: 3,
                }}
                eventHandlers={{ click: () => onToggleSelection?.(r.id) }}
              >
                <Tooltip direction="top">
                  <div style={{ minWidth: 160 }}>
                    <strong>Trip Start</strong><br />
                    {r.startAddress || 'Start'}
                  </div>
                </Tooltip>
              </CircleMarker>
              {/* End marker */}
              <CircleMarker
                center={positions[positions.length - 1]}
                radius={selected ? 10 : 7}
                pathOptions={{
                  color: '#ffffff',
                  fillColor: color,
                  fillOpacity: 1,
                  weight: 3,
                }}
                eventHandlers={{ click: () => onToggleSelection?.(r.id) }}
              >
                <Tooltip direction="top">
                  <div style={{ minWidth: 160 }}>
                    <strong>Trip End</strong><br />
                    {r.endAddress || 'End'}
                  </div>
                </Tooltip>
              </CircleMarker>
            </React.Fragment>
          );
        })}

        {/* Stops (stop mode) */}
        {mode === 'stop' && stops.map((s) => {
          if (!Number(s.lat) || !Number(s.lng)) return null;
          const selected = selectedSet.has(s.id);
          const inHotZone = hotZoneStopIds.has(s.id);
          const color = truckColor[s.truckId] || (selected ? INK : MUTED);
          return (
            <React.Fragment key={s.id}>
              {/* Radius bubble for selected stops */}
              {selected && (
                <Circle
                  center={[s.lat, s.lng]}
                  radius={radiusMeters}
                  pathOptions={{
                    color: BRAND,
                    fillColor: BRAND,
                    fillOpacity: 0.12,
                    weight: 1.5,
                    opacity: 0.5,
                  }}
                />
              )}
              <CircleMarker
                center={[s.lat, s.lng]}
                radius={selected ? 9 : 6}
                pathOptions={{
                  color: '#ffffff',
                  fillColor: color,
                  fillOpacity: 1,
                  weight: selected ? 2.5 : (inHotZone ? 2 : 1.5),
                }}
                eventHandlers={{ click: () => onToggleSelection?.(s.id) }}
              >
                <Tooltip direction="top">
                  <div style={{ minWidth: 180 }}>
                    <strong>{s.address || 'Stop'}</strong><br />
                    <span style={{ fontSize: 12, color: '#666' }}>
                      {s.durationMin > 0 ? `${Math.round(s.durationMin)} min stop` : 'Current position'}
                    </span>
                  </div>
                </Tooltip>
              </CircleMarker>
            </React.Fragment>
          );
        })}
      </MapContainer>

      {/* Recenter / locate buttons */}
      <div className="pointer-events-none absolute right-3 top-3 z-[1000] flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setRecenterSignal(s => s + 1)}
          disabled={stops.length === 0 && routes.length === 0}
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-2xl border-2 border-paper-300 bg-paper-50/95 px-3 py-1.5 text-xs font-semibold text-ink shadow-soft hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
          </svg>
          Recenter on fleet
        </button>
      </div>

      {/* Legend */}
      {stops.length > 0 && (
        <div className="pointer-events-none absolute bottom-3 left-3 z-[1000] rounded-2xl border-2 border-paper-300 bg-paper-50/95 px-3 py-2 text-xs shadow-soft">
          <div className="flex flex-wrap items-center gap-3">
            {mode === 'stop' ? (
              <>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-ink ring-2 ring-paper-50" />
                  <span className="text-ink-100">Stop</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full border-2 border-brand bg-brand/15" />
                  <span className="text-ink-100">Selected</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-full border-2 border-brand bg-brand/15" />
                  <span className="text-ink-100">{radiusMi}-mile pin</span>
                </span>
              </>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-brand ring-2 ring-paper-50" />
                  <span className="text-ink-100">Breadcrumb · every 1mi</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-3 w-3 rounded-full border-2 border-brand bg-brand/15" />
                  <span className="text-ink-100">{radiusMi}-mile pin</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-0.5 w-5 rounded-full bg-ink" />
                  <span className="text-ink-100">Route</span>
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Data check */}
      {mapCenter && (
        <div className="pointer-events-none absolute bottom-3 right-3 z-[1000] rounded-2xl border-2 border-paper-300 bg-paper-50/95 px-3 py-2 text-xs shadow-soft">
          <div className="font-semibold text-ink">Data check</div>
          <div className="mt-1 text-ink-200">
            {stops.length} stops returned by Geotab
            {mapCenter && (
              <>
                <br />Centered at {mapCenter.lat.toFixed(2)}&deg;, {mapCenter.lng.toFixed(2)}&deg;
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
